import './style.css'
import { createClient } from '@supabase/supabase-js'

// Hybrid authentication system:
// - Production: Uses Cloudflare Pages Functions (server-side)
// - Development: Uses direct Supabase calls (client-side)

let supabase = null;
const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

if (isDevelopment) {
    // Development: Use client-side Supabase
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    
    if (supabaseUrl && supabaseKey) {
        try {
            supabase = createClient(supabaseUrl, supabaseKey);
            console.log('Development: Supabase initialized for client-side auth');
        } catch (error) {
            console.error('Failed to initialize Supabase:', error);
        }
    } else {
        console.warn('Development: Supabase environment variables not found');
    }
}

// Initialize animations immediately when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
    try {
        initializeAnimations();
    } catch (error) {
        console.error('Animation initialization failed:', error);
    }
});

// Initialize all animations and interactions
function initializeAnimations() {
    // Device optimizations (run first)
    setupDeviceOptimizations();
    
    // Navigation scroll effect
    setupNavigationScroll();
    
    // Smooth scrolling for navigation links
    setupSmoothScrolling();
    
    // Stats counter animation
    setupStatsCounter();
    
    // Scroll-triggered animations
    setupScrollAnimations();
    
    // Mobile menu toggle
    setupMobileMenu();
}

// Navigation scroll effect
function setupNavigationScroll() {
    const navbar = document.querySelector('.navbar');
    
    window.addEventListener('scroll', () => {
        if (window.scrollY > 50) {
            navbar.classList.add('scrolled');
        } else {
            navbar.classList.remove('scrolled');
        }
    });
}

// Smooth scrolling for navigation links
function setupSmoothScrolling() {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
        anchor.addEventListener('click', function (e) {
            e.preventDefault();
            const target = document.querySelector(this.getAttribute('href'));
            if (target) {
                const offsetTop = target.offsetTop - 80; // Account for fixed navbar
                window.scrollTo({
                    top: offsetTop,
                    behavior: 'smooth'
                });
            }
        });
    });
}

// Animated stats counter
function setupStatsCounter() {
    const statsNumbers = document.querySelectorAll('.stat-number');
    let hasAnimated = false;
    
    const animateCounters = () => {
        if (hasAnimated) return;
        
        statsNumbers.forEach(stat => {
            const target = parseInt(stat.getAttribute('data-target'));
            const duration = 2000; // 2 seconds
            const step = target / (duration / 16); // 60fps
            let current = 0;
            
            const timer = setInterval(() => {
                current += step;
                if (current >= target) {
                    current = target;
                    clearInterval(timer);
                }
                
                // Format numbers with commas for larger values
                if (target >= 1000) {
                    stat.textContent = Math.floor(current).toLocaleString();
                } else {
                    stat.textContent = Math.floor(current);
                }
                
                // Add % sign for percentage values
                if (stat.getAttribute('data-target') === '99') {
                    stat.textContent = Math.floor(current) + '%';
                }
            }, 16);
        });
        
        hasAnimated = true;
    };
    
    // Trigger animation when stats section comes into view
    const statsSection = document.querySelector('.stats');
    const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                animateCounters();
            }
        });
    }, { threshold: 0.5 });
    
    if (statsSection) {
        observer.observe(statsSection);
    }
}

// Scroll-triggered animations for elements
function setupScrollAnimations() {
    const animatedElements = document.querySelectorAll('.feature-card, .step, .section-header');
    
    const observerOptions = {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
    };
    
    const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                entry.target.classList.add('fade-in', 'visible');
                // Add staggered delay for feature cards
                if (entry.target.classList.contains('feature-card')) {
                    const delay = Array.from(entry.target.parentNode.children).indexOf(entry.target) * 100;
                    entry.target.style.transitionDelay = `${delay}ms`;
                }
            }
        });
    }, observerOptions);
    
    animatedElements.forEach(el => {
        el.classList.add('fade-in');
        observer.observe(el);
    });
}

// Mobile menu toggle with enhanced functionality
function setupMobileMenu() {
    const hamburger = document.querySelector('.hamburger');
    const navMenu = document.querySelector('.nav-menu');
    const navLinks = document.querySelectorAll('.nav-link');
    
    if (hamburger && navMenu) {
        // Toggle menu on hamburger click
        hamburger.addEventListener('click', (e) => {
            e.stopPropagation();
            hamburger.classList.toggle('active');
            navMenu.classList.toggle('active');
            
            // Prevent body scroll when menu is open
            if (navMenu.classList.contains('active')) {
                document.body.style.overflow = 'hidden';
            } else {
                document.body.style.overflow = '';
            }
        });
        
        // Close menu when clicking on links
        navLinks.forEach(link => {
            link.addEventListener('click', () => {
                hamburger.classList.remove('active');
                navMenu.classList.remove('active');
                document.body.style.overflow = '';
            });
        });
        
        // Close menu when clicking outside
        document.addEventListener('click', (e) => {
            if (!hamburger.contains(e.target) && !navMenu.contains(e.target)) {
                hamburger.classList.remove('active');
                navMenu.classList.remove('active');
                document.body.style.overflow = '';
            }
        });
        
        // Close menu on escape key
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && navMenu.classList.contains('active')) {
                hamburger.classList.remove('active');
                navMenu.classList.remove('active');
                document.body.style.overflow = '';
            }
        });
        
        // Handle orientation change
        window.addEventListener('orientationchange', () => {
            setTimeout(() => {
                if (window.innerWidth > 768) {
                    hamburger.classList.remove('active');
                    navMenu.classList.remove('active');
                    document.body.style.overflow = '';
                }
            }, 100);
        });
    }
}

// Parallax effect for hero section
function setupParallaxEffect() {
    const heroVisual = document.querySelector('.hero-visual');
    
    window.addEventListener('scroll', () => {
        const scrolled = window.pageYOffset;
        const rate = scrolled * -0.3;
        
        if (heroVisual) {
            heroVisual.style.transform = `translateY(${rate}px)`;
        }
    });
}

// Add interactive hover effects to feature cards
document.addEventListener('DOMContentLoaded', function() {
    const featureCards = document.querySelectorAll('.feature-card');
    
    featureCards.forEach(card => {
        card.addEventListener('mouseenter', function() {
            this.style.transform = 'translateY(-15px) scale(1.02)';
        });
        
        card.addEventListener('mouseleave', function() {
            this.style.transform = 'translateY(0) scale(1)';
        });
    });
});

// Add typing effect to hero title
function setupTypingEffect() {
    const titleLines = document.querySelectorAll('.title-line');
    
    titleLines.forEach((line, index) => {
        const text = line.textContent;
        line.textContent = '';
        line.style.opacity = '1';
        
        setTimeout(() => {
            let charIndex = 0;
            const typeInterval = setInterval(() => {
                line.textContent += text[charIndex];
                charIndex++;
                
                if (charIndex >= text.length) {
                    clearInterval(typeInterval);
                }
            }, 50);
        }, index * 1000);
    });
}

// Enhanced button interactions
document.addEventListener('DOMContentLoaded', function() {
    const buttons = document.querySelectorAll('.btn-primary, .btn-secondary, .btn-primary-large, .nav-cta');
    
    buttons.forEach(button => {
        button.addEventListener('mouseenter', function() {
            this.style.transform = 'translateY(-3px) scale(1.05)';
        });
        
        button.addEventListener('mouseleave', function() {
            this.style.transform = 'translateY(0) scale(1)';
        });
        
        button.addEventListener('mousedown', function() {
            this.style.transform = 'translateY(-1px) scale(1.02)';
        });
        
        button.addEventListener('mouseup', function() {
            this.style.transform = 'translateY(-3px) scale(1.05)';
        });
    });
});

// Add floating animation to golf ball in hero
function setupFloatingAnimation() {
    const golfBallHero = document.querySelector('.golf-ball-hero');
    
    if (golfBallHero) {
        // Add random floating movement
        setInterval(() => {
            const randomX = (Math.random() - 0.5) * 10;
            const randomY = (Math.random() - 0.5) * 10;
            
            golfBallHero.style.transform += ` translate(${randomX}px, ${randomY}px)`;
        }, 3000);
    }
}

// Initialize Supabase connection
// Supabase connection initialized

// Add performance optimization
function setupPerformanceOptimizations() {
    // Lazy load images when they come into view
    const images = document.querySelectorAll('img[data-src]');
    const imageObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                const img = entry.target;
                img.src = img.dataset.src;
                img.classList.remove('lazy');
                imageObserver.unobserve(img);
            }
        });
    });
    
    images.forEach(img => imageObserver.observe(img));
    
    // Preload critical resources
    const criticalResources = ['/logo.png'];
    criticalResources.forEach(resource => {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.href = resource;
        link.as = 'image';
        document.head.appendChild(link);
    });
}

// Initialize everything when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
    setupPerformanceOptimizations();
    setupFloatingAnimation();
});

// Add smooth page transitions
window.addEventListener('beforeunload', function() {
    document.body.style.opacity = '0';
    document.body.style.transition = 'opacity 0.3s ease';
});

// Device detection and optimization
function setupDeviceOptimizations() {
    // Detect device type
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const isTablet = /iPad|Android/i.test(navigator.userAgent) && window.innerWidth >= 768;
    const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    
    // Add device classes to body for CSS targeting
    document.body.classList.add(isMobile ? 'mobile' : 'desktop');
    if (isTablet) document.body.classList.add('tablet');
    if (isTouch) document.body.classList.add('touch');
    
    // Logo fallback system
    setupLogoFallbacks();
    
    // Touch optimizations
    if (isTouch) {
        setupTouchOptimizations();
    }
    
    // Performance optimizations for mobile
    if (isMobile) {
        setupMobilePerformanceOptimizations();
    }
}

// Logo fallback system for different devices
function setupLogoFallbacks() {
    const logos = document.querySelectorAll('.nav-logo, .footer-logo, .logo');
    
    logos.forEach(logo => {
        // Create fallback system - files in public/ are served at root
        const fallbackPaths = [
            '/logo.png',        // Vite serves public/logo.png at /logo.png
            './logo.png',       // Relative path fallback
            'logo.png',         // Simple relative fallback
            '/assets/logo.png'  // In case it gets moved to assets during build
        ];
        
        let currentIndex = 0;
        
        const tryNextImage = () => {
            if (currentIndex < fallbackPaths.length) {
                const img = new Image();
                img.onload = () => {
                    logo.src = fallbackPaths[currentIndex];
                };
                img.onerror = () => {
                    currentIndex++;
                    tryNextImage();
                };
                img.src = fallbackPaths[currentIndex];
            } else {
                // Final fallback: create SVG logo
                createSVGFallback(logo);
            }
        };
        
        // Check if current logo loads, if not start fallback chain
        if (logo.complete && logo.naturalWidth === 0) {
            tryNextImage();
        } else {
            logo.addEventListener('error', tryNextImage);
        }
    });
}

// Create SVG fallback logo
function createSVGFallback(logoElement) {
    const svgLogo = `
        <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="20" cy="20" r="18" fill="#2c4c3b" stroke="#d4af37" stroke-width="2"/>
            <circle cx="20" cy="20" r="3" fill="#ffffff"/>
            <text x="20" y="32" text-anchor="middle" fill="#d4af37" font-family="serif" font-size="8" font-weight="bold">GG</text>
        </svg>
    `;
    
    logoElement.outerHTML = svgLogo;
}

// Touch-specific optimizations
function setupTouchOptimizations() {
    // Improve touch targets
    const buttons = document.querySelectorAll('button, .btn-primary, .btn-secondary, .nav-link');
    buttons.forEach(button => {
        button.style.minHeight = '44px';
        button.style.minWidth = '44px';
    });
    
    // Add touch feedback
    document.addEventListener('touchstart', function(e) {
        if (e.target.matches('button, .btn-primary, .btn-secondary, .nav-cta')) {
            e.target.style.transform = 'scale(0.95)';
        }
    });
    
    document.addEventListener('touchend', function(e) {
        if (e.target.matches('button, .btn-primary, .btn-secondary, .nav-cta')) {
            setTimeout(() => {
                e.target.style.transform = '';
            }, 150);
        }
    });
}

// Mobile performance optimizations
function setupMobilePerformanceOptimizations() {
    // Lazy load images
    const images = document.querySelectorAll('img[data-src]');
    if ('IntersectionObserver' in window) {
        const imageObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const img = entry.target;
                    img.src = img.dataset.src;
                    img.classList.remove('lazy');
                    imageObserver.unobserve(img);
                }
            });
        });
        
        images.forEach(img => imageObserver.observe(img));
    }
    
    // Reduce animations on low-end devices
    if (navigator.hardwareConcurrency && navigator.hardwareConcurrency < 4) {
        document.body.classList.add('reduce-motion');
    }
}
